import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, PhoneOff, Loader2, X, Link as LinkIcon, PlayCircle, Ticket, MessageCircle, Captions, ShieldAlert, Copy, Send } from 'lucide-react';
import { V1_STATS, V2_STATS, COMBINED_STATS, FAQ_DATA } from '../config/orbConfig';
import { SupportVoiceClient } from '../components/support/supportVoiceClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COUNTRY_CODES = ['+91', '+1', '+44', '+971', '+61', '+65', '+49', '+92', '+977', '+880'];
const pickStats = (s) => ({ name: s.name, label: s.label, oneYear: s.oneYear, fiveYear: s.fiveYear, monteCarlo: s.monteCarlo, recommendedRisk: s.recommendedRisk, monthlyPnl_last_12_months: s.monthlyPnl });

// Same numbers the ORB page shows: one source for site and agent
const siteData = () => ({
  performance: { V1: pickStats(V1_STATS), V2: pickStats(V2_STATS), combined: COMBINED_STATS },
  faqs: FAQ_DATA.map((f) => ({ q: f.question, a: f.answer })),
});

const STATUS_TEXT = {
  idle: 'Tap the mic to start', connecting: 'Connecting…', live: 'Listening, go ahead and talk',
  listening: 'Listening…', speaking: 'Speaking…', checking: "Checking the firm's official site…", ended: 'Call ended. Tap the mic to talk again',
  timeup: 'Calls are capped at 10 minutes. Tap the mic to continue, or message Sahil on WhatsApp',
};

const RulesCard = ({ item }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-[#2ecc8f]/25 bg-[#0d1826] p-4" data-testid={`support-resource-${item.id}`}>
    <div className="flex items-center justify-between gap-3 mb-2">
      <p className="text-white text-sm font-semibold flex items-center gap-2"><ShieldAlert size={16} className="text-[#2ecc8f]" />{item.title}</p>
      {item.value && <a href={item.value} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg bg-white/10 text-white text-xs shrink-0" data-testid={`support-rules-source-${item.id}`}>Official site</a>}
    </div>
    <ul className="space-y-1 text-white/75 text-xs leading-relaxed">{item.lines.map((l, i) => <li key={i}>• {l}</li>)}</ul>
    <p className="text-white/40 text-[11px] mt-2">From the firm's official site as of {item.asOf}. Please confirm the current rules there before deciding.</p>
  </motion.div>
);

const ResourceCard = ({ item }) => {
  if (item.type === 'rules') return <RulesCard item={item} />;
  const Icon = item.id === 'telegram' ? Send : item.type === 'video' ? PlayCircle : item.type === 'coupon' ? Ticket : item.type === 'whatsapp' ? MessageCircle : LinkIcon;
  const isCode = item.type === 'coupon';
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-white/10 bg-[#0d1826] p-4 flex items-center gap-3" data-testid={`support-resource-${item.id}`}>
      <span className="w-10 h-10 rounded-lg bg-[#7c3aed]/15 flex items-center justify-center flex-shrink-0"><Icon size={18} className="text-[#a78bfa]" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-medium truncate">{item.title}</p>
        {isCode && <p className="font-mono text-[#2ecc8f] text-sm">{item.value}</p>}
      </div>
      {isCode ? (
        <button onClick={() => navigator.clipboard?.writeText(item.value)} className="px-3 py-2 rounded-lg bg-white/10 text-white text-xs flex items-center gap-1"><Copy size={12} /> Copy</button>
      ) : (
        <a href={item.value} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-lg bg-[#7c3aed] text-white text-xs font-medium hover:bg-[#6d28d9] transition-colors">Open</a>
      )}
    </motion.div>
  );
};

const StartModal = ({ onClose, onStart }) => {
  const [form, setForm] = useState({ name: '', email: '', countryCode: '+91', phone: '', language: 'english' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const field = 'w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#7c3aed]/60';
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try { await onStart(form); } catch (err) { setError(err.message); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="support-start-modal">
      <motion.form initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onSubmit={submit} className="w-full max-w-md rounded-2xl bg-[#0d1826] border border-white/10 p-6 space-y-3">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading font-semibold text-white text-lg">Before we talk</h2>
          <button type="button" onClick={onClose} data-testid="support-modal-close"><X size={18} className="text-white/50" /></button>
        </div>
        <input className={field} placeholder="Your name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="support-name" />
        <input className={field} type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="support-email" />
        <div className="flex gap-2">
          <select className={`${field} w-28`} value={form.countryCode} onChange={(e) => setForm({ ...form, countryCode: e.target.value })} data-testid="support-country-code">
            {COUNTRY_CODES.map((c) => <option key={c} value={c} className="bg-[#0d1826]">{c}</option>)}
          </select>
          <input className={field} type="tel" placeholder="Phone / WhatsApp" required minLength={6} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d ]/g, '') })} data-testid="support-phone" />
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="support-language">
          {[['english', 'English'], ['hindi', 'हिंदी Hindi'], ['hinglish', 'Hinglish']].map(([v, l]) => (
            <button type="button" key={v} onClick={() => setForm({ ...form, language: v })} data-testid={`support-language-${v}`}
              className={`py-3 rounded-xl text-sm font-medium border transition-colors ${form.language === v ? 'bg-[#7c3aed] border-[#7c3aed] text-white' : 'bg-white/5 border-white/10 text-white/70'}`}>{l}</button>
          ))}
        </div>
        <p className="text-white/30 text-xs">More languages (Tamil, Telugu, Marathi, Bengali, Gujarati, Punjabi) coming soon.</p>
        {error && <p className="text-red-400 text-sm" data-testid="support-start-error">{error}</p>}
        <button type="submit" disabled={loading} className="w-full py-3.5 rounded-xl bg-[#2ecc8f] text-[#0a1420] font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="support-get-support-btn">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />} Get Support
        </button>
      </motion.form>
    </div>
  );
};

export default function SupportPage() {
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState('idle');
  const [resources, setResources] = useState([]);
  const [captions, setCaptions] = useState([]);
  const [showCaptions, setShowCaptions] = useState(false);
  const [muted, setMuted] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('https://wa.me/917223863486');
  const [dataAsOf, setDataAsOf] = useState('');
  const [error, setError] = useState('');
  const clientRef = useRef(null);
  const sessionRef = useRef(null);

  useEffect(() => {
    document.title = 'Support · Monk Algo';
    fetch(`${API_URL}/api/support/public-config`).then((r) => r.json()).then((d) => d.whatsapp_url && setWhatsappUrl(d.whatsapp_url)).catch(() => {});
    return () => clientRef.current?.stop();
  }, []);

  const addResource = (item) => setResources((prev) => (prev.some((r) => r.id === item.id) ? prev : [item, ...prev]));

  const connect = async (session) => {
    setError('');
    const client = new SupportVoiceClient({
      session,
      onResource: addResource,
      onHandoff: () => addResource({ id: 'handoff', title: 'Talk to Sahil directly on WhatsApp', type: 'whatsapp', value: session.whatsapp_url || whatsappUrl }),
      onCaption: (role, text) => setCaptions((prev) => [...prev.slice(-30), { role, text }]),
      onStatus: setStatus,
    });
    clientRef.current = client;
    try {
      await client.start();
    } catch (err) {
      client.stop();
      setStatus('idle');
      setError(err.name === 'NotAllowedError' ? 'Please allow microphone access to talk to support.' : err.message);
    }
  };

  const startSupport = async (form) => {
    const res = await fetch(`${API_URL}/api/support/start`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone.replace(/\s/g, ''), countryCode: form.countryCode, language: form.language, site_data: siteData() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check your details and try again.');
    sessionRef.current = data;
    setDataAsOf(data.data_as_of);
    if (data.whatsapp_url) setWhatsappUrl(data.whatsapp_url);
    setShowModal(false);
    await connect(data);
  };

  const onMic = () => {
    if (['connecting', 'live', 'listening', 'speaking', 'checking'].includes(status)) {
      setMuted((m) => { clientRef.current?.setMuted(!m); return !m; });
    } else if (sessionRef.current) {
      connect(sessionRef.current);
    } else {
      setShowModal(true);
    }
  };

  const active = ['live', 'listening', 'speaking', 'connecting', 'checking'].includes(status);

  return (
    <div className="min-h-screen bg-[#0a1420] pt-24 pb-24 px-5" data-testid="support-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <img src="https://monkalgo.com/logo.png" alt="Monk Algo" className="w-10 h-10 rounded-xl" />
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white">Monk Algo Support</h1>
            <p className="text-white/50 text-sm">Talk to our AI assistant in English, Hindi or Hinglish: pricing, plans, licences, prop firms and setup.</p>
          </div>
        </div>

        <div className="flex flex-col items-center text-center">
          <div className="relative">
            {active && !muted && <span className="absolute inset-0 rounded-full bg-[#7c3aed]/30 animate-ping" />}
            <button onClick={onMic} data-testid="support-mic-button"
              className={`relative w-40 h-40 sm:w-48 sm:h-48 rounded-full flex items-center justify-center shadow-2xl transition-[transform,background-color] duration-300 hover:scale-105 ${muted ? 'bg-white/10' : status === 'speaking' ? 'bg-[#2ecc8f]' : 'bg-[#7c3aed]'}`}
              style={{ boxShadow: muted ? 'none' : '0 0 80px rgba(124,58,237,0.45)' }}>
              {status === 'connecting' ? <Loader2 size={56} className="text-white animate-spin" /> : muted ? <MicOff size={56} className="text-white/70" /> : <Mic size={56} className="text-white" />}
            </button>
          </div>
          <p className="mt-6 font-heading font-semibold text-white text-lg" data-testid="support-mic-label">
            {active ? (muted ? 'Muted, tap to unmute' : STATUS_TEXT[status]) : 'Click here to get support'}
          </p>
          {status !== 'idle' && <p className="text-white/40 text-sm mt-1" data-testid="support-status">{active ? 'Voice only · speak naturally' : STATUS_TEXT[status]}</p>}
          {error && <p className="text-red-400 text-sm mt-3" data-testid="support-error">{error}</p>}
          <div className="flex items-center gap-3 mt-5">
            {active && (
              <button onClick={() => clientRef.current?.stop()} className="px-4 py-2 rounded-full bg-red-500/15 text-red-400 text-sm flex items-center gap-2" data-testid="support-end-call"><PhoneOff size={14} /> End call</button>
            )}
            <button onClick={() => setShowCaptions((v) => !v)} className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 ${showCaptions ? 'bg-white/15 text-white' : 'bg-white/5 text-white/50'}`} data-testid="support-captions-toggle">
              <Captions size={14} /> Captions {showCaptions ? 'on' : 'off'}
            </button>
          </div>
          {showCaptions && captions.length > 0 && (
            <div className="mt-4 w-full max-h-40 overflow-y-auto rounded-xl bg-white/[0.03] border border-white/5 p-3 text-left space-y-1" data-testid="support-captions">
              {captions.map((c, i) => <p key={i} className={`text-sm ${c.role === 'user' ? 'text-white/60' : 'text-white'}`}><span className="text-white/30 mr-1">{c.role === 'user' ? 'You:' : 'Agent:'}</span>{c.text}</p>)}
            </div>
          )}
        </div>

        <div className="mt-12" data-testid="support-resource-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white/70 text-sm font-semibold uppercase tracking-wider">Resources</h2>
            {dataAsOf && <span className="font-mono text-white/30 text-xs" data-testid="support-data-as-of">data as of {dataAsOf}</span>}
          </div>
          {resources.length === 0 ? (
            <p className="rounded-xl border border-dashed border-white/10 p-5 text-white/35 text-sm">Links, videos and codes the assistant shares will appear here.</p>
          ) : (
            <div className="space-y-2"><AnimatePresence>{resources.map((r) => <ResourceCard key={r.id} item={r} />)}</AnimatePresence></div>
          )}
        </div>

        <p className="mt-10 text-white/30 text-xs flex items-start gap-2"><ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
          Trading involves risk of loss. Past performance does not guarantee future results. Monk Algo is a tool, not financial advice. Conversations are recorded to help you better.</p>
      </div>

      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="support-whatsapp-button"
        className="fixed bottom-5 right-5 z-50 px-5 py-3.5 rounded-full bg-[#25D366] text-white font-semibold text-sm flex items-center gap-2 shadow-2xl hover:scale-105 transition-transform">
        <MessageCircle size={18} /> Talk to Sahil on WhatsApp
      </a>
      {showModal && <StartModal onClose={() => setShowModal(false)} onStart={startSupport} />}
    </div>
  );
}
